import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import {
  CheckCircleIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/solid';

const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    paymentIntent,
    teacher,
    bookingData,
    studentData,
    lessonRequest
  } = location.state || {};

  // Log payment success page access
  console.log('Payment success page loaded');
  console.log('Payment intent:', paymentIntent);
  console.log('Teacher:', teacher);
  console.log('Booking data:', bookingData);

  if (!paymentIntent || !teacher || !bookingData) {
    console.warn('Missing payment success data, redirecting home');
    navigate('/');
    return null;
  }

  const handleViewDashboard = () => {
    console.log('Navigating to student dashboard');
    navigate('/student-dashboard', {
      state: { studentData }
    });
  };

  const handleContactTeacher = () => {
    console.log('Opening messaging with teacher:', teacher.name);
    navigate('/messages', {
      state: {
        openConversationWith: teacher.name,
        userType: 'teacher'
      }
    });
  };

  const handleGoHome = () => {
    console.log('Navigating to home page');
    navigate('/');
  };

  // Format payment details
  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2); // Convert from cents
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircleIcon className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-lg text-gray-600">
              Your lesson has been booked and payment confirmed.
            </p>
            <div className="mt-4 inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              Payment ID: {paymentIntent.id}
            </div>
          </div>

          {/* Payment Details Card */}
          <div className="bg-white rounded-lg shadow-lg border overflow-hidden mb-8">
            
            {/* Payment Header */}
            <div className="bg-green-50 px-6 py-4 border-b">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCardIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Payment Confirmed</h2>
                  <p className="text-sm text-gray-600">
                    Processed on {formatDate(paymentIntent.created)}
                  </p>
                </div>
              </div>
            </div>

            {/* Lesson and Payment Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Lesson Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Lesson Details</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <UserIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">Teacher</div>
                        <div className="text-gray-600">{teacher.name}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <span className="w-5 h-5 text-gray-400 mt-0.5 text-lg">🎵</span>
                      <div>
                        <div className="font-medium text-gray-900">Instrument</div>
                        <div className="text-gray-600">{paymentIntent.metadata.instrument}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <CalendarDaysIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">Date & Time</div>
                        <div className="text-gray-600">
                          {paymentIntent.metadata.lessonDate}, {paymentIntent.metadata.lessonTime}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <span className="w-5 h-5 text-gray-400 mt-0.5 font-bold">⏱</span>
                      <div>
                        <div className="font-medium text-gray-900">Duration</div>
                        <div className="text-gray-600">{bookingData.duration} minutes</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <span className="w-5 h-5 text-gray-400 mt-0.5 font-bold">📍</span>
                      <div>
                        <div className="font-medium text-gray-900">Lesson Type</div>
                        <div className="text-gray-600">{bookingData.lessonType}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lesson fee</span>
                      <span className="font-medium text-gray-900">
                        ${formatAmount(paymentIntent.amount)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Platform fee</span>
                      <span className="font-medium text-gray-900">$0.00</span>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between">
                        <span className="text-lg font-semibold text-gray-900">Total Paid</span>
                        <span className="text-lg font-bold text-green-600">
                          ${formatAmount(paymentIntent.amount)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Payment Method</span>
                          <span>•••• •••• •••• 4242</span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span>Transaction ID</span>
                          <span className="font-mono">{paymentIntent.id}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow border p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h3>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">1</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Confirmation Email Sent</div>
                  <div className="text-gray-600">
                    A receipt and lesson details have been sent to your email address.
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Teacher Contact</div>
                  <div className="text-gray-600">
                    {teacher.name} will reach out within 24 hours with lesson setup instructions.
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Prepare for Your Lesson</div>
                  <div className="text-gray-600">
                    Make sure you have the necessary materials and a quiet practice space ready.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleViewDashboard}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UserIcon className="w-5 h-5" />
              <span>View My Dashboard</span>
            </button>
            
            <button
              onClick={handleContactTeacher}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              <span>Message Teacher</span>
            </button>
            
            <button
              onClick={handleGoHome}
              className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <HomeIcon className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
          </div>

          {/* Receipt Download */}
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                console.log('Downloading receipt for payment:', paymentIntent.id);
                alert('Receipt download feature coming soon!');
              }}
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <DocumentTextIcon className="w-4 h-4" />
              <span>Download Receipt</span>
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-yellow-50 rounded-lg border border-yellow-200 p-6 text-center">
            <h4 className="font-semibold text-yellow-800 mb-2">Questions about your lesson?</h4>
            <p className="text-yellow-700 mb-4">
              Our support team is here to help ensure you have the best learning experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <a
                href="mailto:support@lessonlynx.com"
                className="text-yellow-800 hover:text-yellow-900 underline"
              >
                support@lessonlynx.com
              </a>
              <span className="hidden sm:inline text-yellow-700">•</span>
              <a
                href="tel:+1-555-LESSON"
                className="text-yellow-800 hover:text-yellow-900 underline"
              >
                (555) LESSON-1
              </a>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default PaymentSuccessPage; 